// src/pages/organization/event/CreateEvent.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProfileImage from '../../../components/profileImage';
import { useMainPageTitle } from "../../../contexts/PageContext";


const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const CreateEvent = () => {
  const { teamId, eventId } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(eventId);
  
  const { updatePageTitle } = useMainPageTitle();
  useEffect(() => {
    updatePageTitle(
      isEditMode ? "Edit Event" : "Create Event", 
      isEditMode ? "Edit event details" : "Create a new event"
    );
  }, [updatePageTitle, isEditMode]);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  // Helper function to get default date/time (2 hours from now)
  const getDefaultDateTime = () => {
    const twoHoursFromNow = new Date(Date.now() + 2 * 60 * 60 * 1000);
    return {
      date: twoHoursFromNow.toISOString().split('T')[0],
      time: twoHoursFromNow.toTimeString().slice(0, 5) // HH:MM format
    };
  };

  const defaultDateTime = getDefaultDateTime();
  
  const [formData, setFormData] = useState({
    name: 'Training',
    description: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
    startDate: defaultDateTime.date,
    startTime: defaultDateTime.time,
    eventType: 'Training',
    teamId: teamId
  });
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // If editing, fetch event data first
        if (isEditMode && eventId) {
          const eventResponse = await fetch(`${API_URL}/event/get-event-by-id?eventId=${eventId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!eventResponse.ok) {
            throw new Error('Failed to fetch event details');
          }
          
          const eventData = await eventResponse.json();
          console.log('Event API Response:', eventData);
          
          if (!eventData.success || !eventData.event) {
            throw new Error('Failed to fetch event data from API');
          }
          
          const event = eventData.event;
          console.log('Extracted event data:', event);
          
          if (!event.startDate) {
            throw new Error('Event startDate is missing');
          }
          
          // Parse the start date and time
          const startDate = new Date(event.startDate);
          const formattedDate = startDate.toISOString().split('T')[0];
          const formattedTime = startDate.toTimeString().slice(0, 5);
          
          // Get the team ID from the teams array (first team for single team events, or could be either for matches)
          const eventTeamId = event.teams && event.teams.length > 0 ? event.teams[0] : null;
          
          setFormData({
            name: event.name,
            description: event.description,
            startDate: formattedDate,
            startTime: formattedTime,
            eventType: event.eventType,
            teamId: eventTeamId
          });
          
          // Set the teamId for team fetching
          const currentTeamId = eventTeamId;
          
          // Fetch team data
          if (currentTeamId) {
            const teamResponse = await fetch(`${API_URL}/team/show-team-by-team-id?teamId=${currentTeamId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (!teamResponse.ok) {
              console.warn('Failed to fetch team details, event may not have a valid team assigned');
              setTeam(null);
            } else {
              const teamData = await teamResponse.json();
              setTeam(teamData.team);
            }
          } else {
            console.warn('Event has no team assigned');
            setTeam(null);
          }
        } else {
          // Creating new event
          if (!teamId) {
            navigate('/organization/events/show-teams');
            return;
          }

          const teamResponse = await fetch(`${API_URL}/team/show-team-by-team-id?teamId=${teamId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!teamResponse.ok) {
            throw new Error('Failed to fetch team details');
          }
          
          const teamData = await teamResponse.json();
          setTeam(teamData.team);
          
          setFormData(prev => ({
            ...prev,
            teamId: teamId
          }));
        }
      } catch (error) {
        setError(error.message);
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [teamId, eventId, isEditMode, token, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create date in local timezone and ensure it's in the future
      const localDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
      
      // Check if the selected date/time is in the future (allow at least 1 hour from now)
      const now = new Date();
      const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000); // Add 1 hour
      
      if (localDateTime < oneHourFromNow) {
        const minDateTime = oneHourFromNow.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM
        setError(`Please select a date and time at least 1 hour from now. Minimum: ${new Date(minDateTime).toLocaleString()}`);
        return;
      }
      
      // Convert to UTC for server - but keep the intended local time
      // by adjusting for timezone offset
      const timezoneOffset = localDateTime.getTimezoneOffset() * 60000; // Convert to milliseconds
      const utcDateTime = new Date(localDateTime.getTime() - timezoneOffset);
      
      // Debug logs
      console.log('Form Data:', formData);
      console.log('Team ID:', teamId);
      console.log('Local Date Time:', localDateTime);
      console.log('UTC Date Time:', utcDateTime.toISOString());
      console.log('API URL:', `${API_URL}/event/create-event`);

      const requestBody = {
        name: formData.name,
        description: formData.description,
        startDate: utcDateTime.toISOString(),
        eventType: formData.eventType,
        teamId: formData.teamId,
        ...(isEditMode && { eventId: eventId }) // Include eventId for update requests
      };

      console.log('Request Body:', requestBody);

      // Clear any previous errors
      setError(null);

      const apiEndpoint = isEditMode 
        ? `${API_URL}/event/update-event-by-id?eventId=${eventId}`
        : `${API_URL}/event/create-event`;
      
      const method = isEditMode ? "PUT" : "POST";

      const response = await fetch(apiEndpoint, {
        method: method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response Status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.log('Error Data:', errorData);
        
        // Handle detailed validation errors
        if (errorData.details && Array.isArray(errorData.details)) {
          const validationErrors = errorData.details.map(detail => detail.message || detail).join(', ');
          throw new Error(`Validation failed: ${validationErrors}`);
        }
        
        throw new Error(errorData.message || "Failed to create event");
      }

      const data = await response.json();
      console.log('Response Data:', data);
      
      if (isEditMode) {
        // For edit mode, data structure might be different
        const updatedEventId = data.event?._id || data._id || eventId;
        console.log('Updated Event ID:', updatedEventId);
        navigate(`/organization/events/${updatedEventId}`);
      } else {
        // For create mode
        if (!data || !data.event) {
          throw new Error("No event data received from server");
        }
        console.log('Created Event ID:', data.event._id);
        navigate(`/organization/events/${data.event._id}`);
      }
      
    } catch (err) {
      console.error("Full error:", err);
      setError(err.message || "Failed to create event. Please try again later.");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F15213]"></div>
      </div>
    );
  }

  if (!team && !isEditMode) {
    return (
      <div className="flex justify-center items-center h-64 text-red-500">
        <p>Team not found</p>
      </div>
    );
  }

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-6">
            {team && (
              <div className="relative">
                <ProfileImage
                  image={team.logo}
                  alt={team.name}
                  className="w-28 h-28 rounded-xl object-cover shadow-md"
                />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                {isEditMode ? "Edit Event" : "Create New Event"}
              </h1>
              <p className="text-gray-500 mt-1">
                {team ? `for ${team.name}` : "No team assigned"}
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Event Name */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Event Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                placeholder="Enter event name"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                placeholder="Describe your event"
                required
              />
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <label className="block text-gray-700 font-semibold mb-2">
                  Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                    required
                  />
                </div>
              </div>

              <div className="relative">
                <label className="block text-gray-700 font-semibold mb-2">
                  Time
                </label>
                <div className="relative">
                  <input
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Event Type */}
            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Event Type
              </label>
              <div className="relative">
                <select
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 appearance-none"
                  required
                >
                  <option value="Event">Event</option>
                  <option value="Training">Training</option>
                </select>
                <div className="absolute right-3 top-3 text-gray-400 pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                className="w-full bg-[#F15213] text-white py-4 px-6 rounded-xl hover:bg-[#F15213]/80 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition duration-200 hover:scale-[1.02] active:scale-[0.98] font-semibold text-lg shadow-lg"
              >
                Create Event
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEvent;
